import { useEffect, useState } from "react";
import { Tab as MuiTab, Tabs as MuiTabs } from "@mui/material";
import {
  AttachFile as MuiAttachFileIcon,
  ErrorOutlineOutlined as ErrorOutlineIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Theme } from "common";

// Styles for MUI Tabs
const tabStyles = {
  textTransform: "none",
  alignItems: "flex-start",
  textAlign: "left",
  fontWeight: 600,
  color: Theme.palette.primary.dark,
  "&.MuiTab-labelIcon": {
    gap: 1,
    minHeight: "20px",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  "&.Mui-selected": {
    color: Theme.palette.primary.dark,
    backgroundColor: Theme.palette.common.white,
  },
};

export const FormNavigationPanel = ({
  actionId,
  hasFormErrors,
  navigableSections,
  setActiveSection,
}) => {
  const { t } = useTranslation();
  // State to manage current tab index
  const [tabIndex, setTabIndex] = useState(false);
  // Handle tab change event
  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
  };
  // Handle click on each tab to scroll to the respective section
  const handleTabClick = (section) => {
    setActiveSection(section);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  // Attachment URL
  const attachmentUrl = `/file-attachments/${actionId}`;

  // Calculate total tabs
  const totalTabs =
    navigableSections.length + (actionId ? 1 : 0) + (hasFormErrors ? 1 : 0);

  // Ensure tabIndex is valid before rendering
  const validTabIndex =
    typeof tabIndex === "number" && tabIndex < totalTabs ? tabIndex : false;

  // Effect to reset tab index if it becomes invalid
  useEffect(() => {
    if (typeof tabIndex === "number" && tabIndex >= totalTabs) {
      setTabIndex(false);
    }
  }, [navigableSections, hasFormErrors, actionId, tabIndex, totalTabs]);

  return (
    <MuiTabs
      orientation="vertical"
      variant="scrollable"
      value={validTabIndex}
      onChange={handleTabChange}
      aria-label={t("navigationPanel.tabs")}
      slotProps={{
        indicator: {
          style: { display: "none" },
        },
      }}
    >
      {navigableSections?.map((section, index) => (
        <MuiTab
          key={section}
          label={section}
          sx={tabStyles}
          onClick={() => handleTabClick(section)}
        />
      ))}
      {actionId && (
        <MuiTab
          sx={tabStyles}
          key={t("navigationPanel.attachments")}
          label={t("navigationPanel.attachments")}
          component="a"
          target="_blank"
          href={attachmentUrl}
          rel="noopener noreferrer"
          icon={<MuiAttachFileIcon fontSize="small" />}
        />
      )}
      {hasFormErrors && (
        <MuiTab
          sx={tabStyles}
          key={t("navigationPanel.formErrors")}
          label={t("navigationPanel.formErrors")}
          icon={<ErrorOutlineIcon fontSize="small" color="error" />}
          onClick={() => handleTabClick("Form Errors")}
        />
      )}
    </MuiTabs>
  );
};
